import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  projectReferencesService,
  type ProjectReferencesListResult,
  type ProjectReferenceWarning,
} from '@/shared/api/services/project-references.service';
import { toast } from '@/shared/lib/toast';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { ProjectReferenceCategory } from '@/types/project-reference';

// Hook for fetching all project references
// Automatically polls when there are pending/processing references
export function useProjectReferences(
  projectId: string,
  options?: { enabled?: boolean; enablePolling?: boolean }
) {
  // Track when pending references were first detected for initial delay
  const pollingStartRef = useRef<number>(0);

  return useQuery({
    queryKey: cacheKeys.references.list(projectId),
    queryFn: () => projectReferencesService.listProjectReferences(projectId),
    select: (result) => result.references,
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: true,
    // Only poll if explicitly enabled AND there are pending references
    // WebSocket delivers instant updates; polling is a safety net
    refetchInterval: options?.enablePolling
      ? (query) => {
          const result = query.state.data as
            | ProjectReferencesListResult
            | undefined;
          const hasPending = result?.references?.some(
            (ref) =>
              ref.attributes.classification_status === 'pending' ||
              ref.attributes.classification_status === 'processing'
          );
          if (!hasPending) {
            pollingStartRef.current = 0;
            return false;
          }
          // Track when polling started
          if (pollingStartRef.current === 0) {
            pollingStartRef.current = Date.now();
          }
          // 20s initial delay (classification takes 15-25s), then 5s fallback polling
          const elapsed = Date.now() - pollingStartRef.current;
          return elapsed < 20_000 ? Math.max(20_000 - elapsed, 5_000) : 5_000;
        }
      : false,
  });
}

// Hook for fetching warnings from the references endpoint
// Shares cache with useProjectReferences
export function useProjectReferenceWarnings(
  projectId: string
): ProjectReferenceWarning[] {
  const { data } = useQuery({
    queryKey: cacheKeys.references.list(projectId),
    queryFn: () => projectReferencesService.listProjectReferences(projectId),
    select: (result) => result.warnings,
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
  return data ?? [];
}

// Hook for fetching references by category
export function useProjectReferencesByCategory(
  projectId: string,
  category: ProjectReferenceCategory,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: cacheKeys.references.byCategory(projectId, category),
    queryFn: () =>
      projectReferencesService.getProjectReferencesByCategory(
        projectId,
        category
      ),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// Hook for fetching a single project reference
export function useProjectReference(
  projectId: string,
  referenceId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: cacheKeys.references.detail(projectId, referenceId),
    queryFn: () =>
      projectReferencesService.getProjectReference(projectId, referenceId),
    enabled: !!projectId && !!referenceId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for creating a project reference
export function useCreateProjectReference(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'create'],
    mutationFn: (data: {
      md5: string;
      fileName?: string;
      contentType: string;
      fileSize: number;
      pageCount?: number;
    }) => projectReferencesService.createProjectReference(projectId, data),
    onSuccess: () => {
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(projectId),
      });
    },
  });
}

// Hook for confirming upload
export function useConfirmProjectReferenceUpload(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'confirm-upload'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: (referenceId: string) =>
      projectReferencesService.confirmUpload(projectId, referenceId),
    onSuccess: (reference) => {
      // Update the cache with the confirmed reference
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(projectId),
      });
    },
  });
}

// Hook for updating reference category
export function useUpdateProjectReferenceCategory(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'update-category'],
    meta: { lockId: (vars: unknown) => (vars as { referenceId: string }).referenceId },
    mutationFn: ({
      referenceId,
      category,
    }: {
      referenceId: string;
      category: ProjectReferenceCategory;
    }) =>
      projectReferencesService.updateCategory(projectId, referenceId, category),
    onSuccess: (reference) => {
      // Update cache
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(projectId),
      });
    },
  });
}

// Hook for updating reference attributes (category + description)
export function useUpdateProjectReference(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'update'],
    meta: { lockId: (vars: unknown) => (vars as { referenceId: string }).referenceId },
    mutationFn: ({
      referenceId,
      category,
      description,
    }: {
      referenceId: string;
      category?: ProjectReferenceCategory;
      description?: string | null;
    }) => {
      const attrs: {
        category?: ProjectReferenceCategory;
        description?: string | null;
      } = {};
      if (category !== undefined) attrs.category = category;
      if (description !== undefined) attrs.description = description;
      return projectReferencesService.updateReference(
        projectId,
        referenceId,
        attrs
      );
    },
    onSuccess: (reference) => {
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(projectId),
      });
    },
  });
}

// Hook for deleting a project reference with undo support
export function useDeleteProjectReference(projectId: string) {
  const queryClient = useQueryClient();
  const t = useTranslations('projectReference');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['references', 'delete'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: (referenceId: string) =>
      projectReferencesService.deleteProjectReference(projectId, referenceId),
    onSuccess: (_, referenceId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: cacheKeys.references.detail(projectId, referenceId),
      });
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(projectId),
      });

      // Show undo toast
      toast.undoable(t('referenceDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await projectReferencesService.restoreProjectReference(
                projectId,
                referenceId
              );
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t('failedToRestoreReference')
              );
            }
            queryClient.invalidateQueries({
              queryKey: cacheKeys.references.list(projectId),
            });
          },
        },
      });
    },
  });
}

// Helper hook to get classified references by category
// Used in render settings selector - includes both user and system refs
export function useClassifiedProjectReferences(
  projectId: string,
  category?: ProjectReferenceCategory,
  options?: { enabled?: boolean }
) {
  const { data: references, ...rest } = useProjectReferences(
    projectId,
    options
  );

  const filteredReferences = references?.filter((ref) => {
    // Only completed classifications (ready to use)
    if (ref.attributes.classification_status !== 'completed') return false;
    // Filter by category if specified
    if (category && ref.attributes.category !== category) return false;
    return true;
  });

  return {
    data: filteredReferences,
    ...rest,
  };
}
